import { useState, useRef, useEffect, useMemo } from 'react';
import { useLabelQueue } from '@/contexts/LabelQueueContext';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Printer, Settings, X, ChevronUp, ChevronDown, Trash2, Save, Plus, Edit2, Copy, Tags, Users } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';

// Label Preset interface - stores core label configuration only
interface LabelPreset {
  id: string;
  name: string;
  width: number;
  height: number;
  is_system: number;
  dpi?: number;
  code_type?: 'barcode' | 'qrcode';
  code_size?: number;
}

interface PrinterSettings {
  labelPreset: string; // Default preset ID
  locationLabelPreset: string; // Preset for location labels
  itemLabelPreset: string; // Preset for individual items
  binLabelPreset: string; // Preset for bin labels
  dpi: string;
  codeType: 'barcode' | 'qrcode';
  codeSize: string;
  fontSize: string;
  titleSizeMultiplier: string;
  locationSizeMultiplier: string;
  subtitleSizeMultiplier: string;
  textCodeGap: string;
  codeSubtitleGap: string;
  offsetX: string;
  offsetY: string;
  showLocation: boolean;
}

const DEFAULT_SETTINGS: PrinterSettings = {
  labelPreset: '',
  locationLabelPreset: '',
  itemLabelPreset: '',
  binLabelPreset: '',
  dpi: '203',
  codeType: 'barcode',
  codeSize: '40',
  fontSize: '8',
  titleSizeMultiplier: '4',
  locationSizeMultiplier: '3',
  subtitleSizeMultiplier: '3.5',
  textCodeGap: '10',
  codeSubtitleGap: '5',
  offsetX: '0',
  offsetY: '0',
  showLocation: true,
};

// Helper to safely parse numbers, return default if invalid
const safeParseFloat = (value: string | number | undefined | null, defaultValue: number): number => {
  if (value === undefined || value === null) return defaultValue;
  const strValue = typeof value === 'string' ? value : String(value);
  if (strValue.trim() === '') return defaultValue;
  const parsed = parseFloat(strValue);
  return isNaN(parsed) ? defaultValue : parsed;
};

export function Labels() {
  const { labels, removeLabel, clearQueue } = useLabelQueue();
  const { addToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<PrinterSettings>(DEFAULT_SETTINGS);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isRendering, setIsRendering] = useState(false);

  // Preset management state
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [showManagePresetsModal, setShowManagePresetsModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState<LabelPreset | null>(null);
  const [isCreatingSystemPreset, setIsCreatingSystemPreset] = useState(false);
  const [presetForm, setPresetForm] = useState({
    name: '',
    width: '',
    height: '',
    dpi: '203',
    codeType: 'barcode' as 'barcode' | 'qrcode',
    codeSize: '40',
  });

  const isAdmin = user?.role === 'admin';

  // Load label presets
  const { data: presets = [], isLoading: presetsLoading } = useQuery({
    queryKey: ['label-presets'],
    queryFn: async () => {
      const res = await api.get<LabelPreset[]>('/users/me/label-presets');
      if (res.success) return res.data || [];
      throw new Error(res.error);
    },
  });

  // Memoized current preset for reactive dimension display
  const currentPreset = useMemo(() => {
    return presets.find(p => p.id === settings.labelPreset);
  }, [presets, settings.labelPreset]);

  // Preset mutations
  const createPresetMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      width: number;
      height: number;
      dpi?: number;
      code_type?: 'barcode' | 'qrcode';
      code_size?: number;
    }) => {
      const res = await api.post<{ id: string }>('/users/me/label-presets', data);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async (data) => {
      // Force refetch of presets to get the new one with all its data
      await queryClient.refetchQueries({ queryKey: ['label-presets'] });
      setShowPresetModal(false);
      setPresetForm({ name: '', width: '', height: '', dpi: '203', codeType: 'barcode', codeSize: '40' });

      // Automatically select the newly created preset
      if (data?.id) {
        // Small delay to ensure state has updated
        setTimeout(() => {
          applyPresetSettings(data.id);
        }, 150);
      }

      addToast({ type: 'success', title: 'Preset created', duration: 2000 });
    },
  });

  const updatePresetMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      name: string;
      width: number;
      height: number;
      dpi?: number;
      code_type?: 'barcode' | 'qrcode';
      code_size?: number;
    }) => {
      const { id, ...payload } = data;
      const res = await api.put(`/users/me/label-presets/${id}`, payload);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async (_, variables) => {
      // Force refetch to get updated preset data
      await queryClient.refetchQueries({ queryKey: ['label-presets'] });
      setShowPresetModal(false);
      setEditingPreset(null);
      setPresetForm({ name: '', width: '', height: '', dpi: '203', codeType: 'barcode', codeSize: '40' });

      // Reapply the updated preset to refresh the UI
      setTimeout(() => {
        applyPresetSettings(variables.id);
      }, 150);

      addToast({ type: 'success', title: 'Preset updated', duration: 2000 });
    },
  });

  const deletePresetMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/users/me/label-presets/${id}`);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['label-presets'] });
      addToast({ type: 'success', title: 'Preset deleted', duration: 2000 });
    },
    onError: (error) => {
      addToast({ type: 'error', title: 'Failed to delete preset', message: error.message, duration: 3000 });
    },
  });

  const duplicatePresetMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/users/me/label-presets/${id}/duplicate`, {});
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['label-presets'] });
      addToast({ type: 'success', title: 'Preset duplicated', duration: 2000 });
    },
  });

  // Admin mutations for system-wide presets
  const createSystemPresetMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      width: number;
      height: number;
      dpi?: number;
      code_type?: 'barcode' | 'qrcode';
      code_size?: number;
    }) => {
      const res = await api.post<{ userCount: number }>('/admin/label-presets', data);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async (data) => {
      await queryClient.refetchQueries({ queryKey: ['label-presets'] });
      setShowPresetModal(false);
      setIsCreatingSystemPreset(false);
      setPresetForm({ name: '', width: '', height: '', dpi: '203', codeType: 'barcode', codeSize: '40' });
      addToast({ type: 'success', title: `System preset deployed to ${data?.userCount || 0} users`, duration: 3000 });
    },
    onError: (error) => {
      addToast({ type: 'error', title: 'Failed to create system preset', message: error.message, duration: 3000 });
    },
  });

  const updateSystemPresetMutation = useMutation({
    mutationFn: async (data: {
      oldName: string;
      oldWidth: number;
      oldHeight: number;
      newName: string;
      newWidth: number;
      newHeight: number;
      dpi?: number;
      code_type?: 'barcode' | 'qrcode';
      code_size?: number;
    }) => {
      const res = await api.put<{ affectedUsers: number }>('/admin/label-presets', data);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async (data) => {
      await queryClient.refetchQueries({ queryKey: ['label-presets'] });
      setShowPresetModal(false);
      setEditingPreset(null);
      setIsCreatingSystemPreset(false);
      setPresetForm({ name: '', width: '', height: '', dpi: '203', codeType: 'barcode', codeSize: '40' });
      addToast({ type: 'success', title: `System preset updated for ${data?.affectedUsers || 0} users`, duration: 3000 });
    },
    onError: (error) => {
      addToast({ type: 'error', title: 'Failed to update system preset', message: error.message, duration: 3000 });
    },
  });

  const deleteSystemPresetMutation = useMutation({
    mutationFn: async (data: { name: string; width: number; height: number }) => {
      const res = await api.delete<{ affectedUsers: number }>('/admin/label-presets', data);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['label-presets'] });
      addToast({ type: 'success', title: `System preset deleted from ${data?.affectedUsers || 0} users`, duration: 3000 });
    },
    onError: (error) => {
      addToast({ type: 'error', title: 'Failed to delete system preset', message: error.message, duration: 3000 });
    },
  });

  // Load settings from database on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get<PrinterSettings>('/users/me/label-settings');
        if (res.success && res.data) {
          const normalized: PrinterSettings = {
            labelPreset: res.data.labelPreset || (presets[0]?.id ?? ''),
            locationLabelPreset: res.data.locationLabelPreset || (presets[0]?.id ?? ''),
            itemLabelPreset: res.data.itemLabelPreset || (presets[0]?.id ?? ''),
            binLabelPreset: res.data.binLabelPreset || (presets[0]?.id ?? ''),
            dpi: String(res.data.dpi || DEFAULT_SETTINGS.dpi),
            codeType: res.data.codeType || DEFAULT_SETTINGS.codeType,
            codeSize: String(res.data.codeSize || DEFAULT_SETTINGS.codeSize),
            fontSize: String(res.data.fontSize || DEFAULT_SETTINGS.fontSize),
            titleSizeMultiplier: String(res.data.titleSizeMultiplier || DEFAULT_SETTINGS.titleSizeMultiplier),
            locationSizeMultiplier: String(res.data.locationSizeMultiplier || DEFAULT_SETTINGS.locationSizeMultiplier),
            subtitleSizeMultiplier: String(res.data.subtitleSizeMultiplier || DEFAULT_SETTINGS.subtitleSizeMultiplier),
            textCodeGap: String(res.data.textCodeGap || DEFAULT_SETTINGS.textCodeGap),
            codeSubtitleGap: String(res.data.codeSubtitleGap || DEFAULT_SETTINGS.codeSubtitleGap),
            offsetX: String(res.data.offsetX || DEFAULT_SETTINGS.offsetX),
            offsetY: String(res.data.offsetY || DEFAULT_SETTINGS.offsetY),
            showLocation: res.data.showLocation ?? DEFAULT_SETTINGS.showLocation,
          };
          setSettings(normalized);
        } else if (presets.length > 0) {
          // No saved settings, default to first preset
          setSettings(s => ({
            ...s,
            labelPreset: presets[0].id,
            locationLabelPreset: presets[0].id,
            itemLabelPreset: presets[0].id,
            binLabelPreset: presets[0].id,
          }));
        }
      } catch (error) {
        console.error('Failed to load label settings:', error);
      } finally {
        setLoadingSettings(false);
      }
    };
    if (!presetsLoading) {
      loadSettings();
    }
  }, [presetsLoading, presets]);

  // Save settings to database
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await api.put('/users/me/label-settings', settings);
      if (res.success) {
        addToast({
          type: 'success',
          title: 'Settings saved',
          message: 'Printer settings have been saved to your account.',
          duration: 2000,
        });
      } else {
        const errorMsg = typeof res.error === 'object'
          ? JSON.stringify(res.error)
          : String(res.error || 'An error occurred');
        addToast({
          type: 'error',
          title: 'Failed to save settings',
          message: errorMsg,
          duration: 5000,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Failed to save settings',
        message: error instanceof Error ? error.message : 'Network error',
        duration: 3000,
      });
    } finally {
      setSavingSettings(false);
    }
  };

  // Get current label dimensions
  const getLabelDimensions = () => {
    const preset = presets.find(p => p.id === settings.labelPreset);
    return preset ? { width: preset.width, height: preset.height } : { width: 2, height: 1 };
  };

  // Apply preset - loads core settings from preset (DPI, code type, code size)
  // Fine-tuning adjustments (font sizes, spacing) remain as user overrides
  const applyPresetSettings = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;

    setSettings(s => ({
      ...s,
      labelPreset: presetId,
      // Core preset settings
      dpi: String(preset.dpi || DEFAULT_SETTINGS.dpi),
      codeType: preset.code_type || DEFAULT_SETTINGS.codeType,
      codeSize: String(preset.code_size || DEFAULT_SETTINGS.codeSize),
      // Keep user's fine-tuning adjustments (don't override)
      fontSize: s.fontSize,
      titleSizeMultiplier: s.titleSizeMultiplier,
      locationSizeMultiplier: s.locationSizeMultiplier,
      subtitleSizeMultiplier: s.subtitleSizeMultiplier,
      textCodeGap: s.textCodeGap,
      codeSubtitleGap: s.codeSubtitleGap,
      offsetX: s.offsetX,
      offsetY: s.offsetY,
    }));
  };

  // Preset modal handlers
  const openCreatePresetModal = (asSystemPreset = false) => {
    setEditingPreset(null);
    setIsCreatingSystemPreset(asSystemPreset);
    setPresetForm({
      name: '',
      width: '',
      height: '',
      dpi: currentPreset?.dpi?.toString() || settings.dpi || '203',
      codeType: currentPreset?.code_type || settings.codeType || 'barcode',
      codeSize: currentPreset?.code_size?.toString() || settings.codeSize || '40',
    });
    setShowManagePresetsModal(false); // Close manage modal when opening create
    setShowPresetModal(true);
  };

  const openEditPresetModal = (preset: LabelPreset) => {
    setEditingPreset(preset);
    setIsCreatingSystemPreset(preset.is_system === 1);
    setPresetForm({
      name: preset.name,
      width: String(preset.width),
      height: String(preset.height),
      dpi: String(preset.dpi || 203),
      codeType: preset.code_type || 'barcode',
      codeSize: String(preset.code_size || 40),
    });
    setShowPresetModal(true);
  };

  const handlePresetSubmit = () => {
    const width = parseFloat(presetForm.width);
    const height = parseFloat(presetForm.height);
    const dpi = parseInt(presetForm.dpi);
    const codeSize = parseInt(presetForm.codeSize);

    if (!presetForm.name || isNaN(width) || isNaN(height) || width <= 0 || height <= 0 || isNaN(dpi) || dpi <= 0 || isNaN(codeSize) || codeSize <= 0) {
      addToast({ type: 'error', title: 'Invalid input', message: 'Please enter valid values for all fields', duration: 3000 });
      return;
    }

    // Preset stores only core label configuration
    const presetData = {
      name: presetForm.name,
      width,
      height,
      dpi,
      code_type: presetForm.codeType,
      code_size: codeSize,
    };

    if (isCreatingSystemPreset) {
      // Admin operations for system presets
      if (editingPreset) {
        updateSystemPresetMutation.mutate({
          oldName: editingPreset.name,
          oldWidth: editingPreset.width,
          oldHeight: editingPreset.height,
          newName: presetData.name,
          newWidth: presetData.width,
          newHeight: presetData.height,
          dpi: presetData.dpi,
          code_type: presetData.code_type,
          code_size: presetData.code_size,
        });
      } else {
        createSystemPresetMutation.mutate(presetData);
      }
    } else {
      // Regular user operations for personal presets
      if (editingPreset) {
        updatePresetMutation.mutate({ id: editingPreset.id, ...presetData });
      } else {
        createPresetMutation.mutate(presetData);
      }
    }
  };

  // Render label to canvas (converts SVG to canvas for preview)
  useEffect(() => {
    if (!canvasRef.current || labels.length === 0) return;

    const renderLabel = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      try {
        const svgString = await getPrintSVG(previewIndex);
        const dims = getLabelDimensions();
        const PREVIEW_DPI = 150;
        const width = Math.round((dims.width || 2) * PREVIEW_DPI);
        const height = Math.round((dims.height || 1) * PREVIEW_DPI);

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        const img = new Image();
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        img.onload = () => {
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);

          ctx.strokeStyle = '#cccccc';
          ctx.lineWidth = 2;
          ctx.strokeRect(0, 0, width, height);
        };

        img.src = url;
      } catch (error) {
        console.error('Preview render error:', error);
      }
    };

    renderLabel();
  }, [previewIndex, settings, labels, presets]);

  // Create SVG for printing
  const getPrintSVG = async (labelIndex: number): Promise<string> => {
    const label = labels[labelIndex];
    const dims = getLabelDimensions();
    const width = dims.width || 2;
    const height = dims.height || 1;

    const widthPt = width * 72;
    const heightPt = height * 72;
    const padding = 7.2;
    const gap = safeParseFloat(settings.textCodeGap, 10);

    const baseFontSize = safeParseFloat(settings.fontSize, 8);
    const titleSize = baseFontSize * safeParseFloat(settings.titleSizeMultiplier, 4);
    const locationSize = baseFontSize * safeParseFloat(settings.locationSizeMultiplier, 3);
    const subtitleSize = baseFontSize * safeParseFloat(settings.subtitleSizeMultiplier, 3.5);

    const offsetX = safeParseFloat(settings.offsetX, 0);
    const offsetY = safeParseFloat(settings.offsetY, 0);
    const codeSubtitleGap = safeParseFloat(settings.codeSubtitleGap, 5);

    let currentY = padding + titleSize;
    let svgContent = '';
    const centerX = widthPt / 2;

    svgContent += `<text x="${centerX}" y="${currentY}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="${titleSize}pt" font-weight="bold" fill="#000000">${escapeXml(label.title)}</text>`;
    currentY += titleSize * 0.4;

    if (settings.showLocation && label.location && label.location.trim() !== '') {
      currentY += locationSize + padding / 2;
      svgContent += `<text x="${centerX}" y="${currentY}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="${locationSize}pt" font-style="italic" fill="#000000">${escapeXml(label.location)}</text>`;
      currentY += locationSize * 0.4;
    }

    currentY += gap;

    if (settings.codeType === 'qrcode') {
      const qrSizePct = safeParseFloat(settings.codeSize, 40) / 100;
      const qrSizePt = Math.min(widthPt, heightPt) * qrSizePct;
      const qrCanvas = document.createElement('canvas');
      const qrPixelSize = Math.round(qrSizePt * 8);
      await QRCode.toCanvas(qrCanvas, label.code, {
        width: qrPixelSize,
        margin: 0,
        errorCorrectionLevel: 'H',
      });
      const qrDataUrl = qrCanvas.toDataURL('image/png');
      const qrX = (widthPt - qrSizePt) / 2;
      svgContent += `<image x="${qrX}" y="${currentY}" width="${qrSizePt}" height="${qrSizePt}" href="${qrDataUrl}" image-rendering="optimizeQuality" />`;
      currentY += qrSizePt + 4;

      currentY += subtitleSize;
      svgContent += `<text x="${centerX}" y="${currentY}" text-anchor="middle" font-family="Courier New, Consolas, monospace" font-size="${subtitleSize}pt" font-weight="bold" fill="#000000">${escapeXml(label.code)}</text>`;
      currentY += subtitleSize * 0.4 + codeSubtitleGap;
    } else {
      const barcodeHeightPct = safeParseFloat(settings.codeSize, 40) / 100;
      const barcodeHeightPt = heightPt * barcodeHeightPct;
      const barcodeSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      JsBarcode(barcodeSvg, label.code, {
        format: 'CODE128',
        width: 2,
        height: barcodeHeightPt - padding,
        displayValue: true,
        fontSize: subtitleSize,
        textMargin: 2,
        margin: 0,
        fontOptions: 'bold',
      });
      const barcodeContent = barcodeSvg.innerHTML;
      const barcodeWidth = parseFloat(barcodeSvg.getAttribute('width') || '0');
      const barcodeHeight = parseFloat(barcodeSvg.getAttribute('height') || '0');
      const barcodeX = (widthPt - barcodeWidth) / 2;
      svgContent += `<g transform="translate(${barcodeX},${currentY})">${barcodeContent}</g>`;
      currentY += barcodeHeight + codeSubtitleGap;
    }

    if (label.subtitle && label.subtitle.trim() !== '') {
      currentY += subtitleSize;
      svgContent += `<text x="${centerX}" y="${currentY}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="${subtitleSize}pt" fill="#000000">${escapeXml(label.subtitle)}</text>`;
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}in" height="${height}in" viewBox="0 0 ${widthPt} ${heightPt}">
      <rect width="${widthPt}" height="${heightPt}" fill="#ffffff"/>
      <g transform="translate(${offsetX},${offsetY})">
        ${svgContent}
      </g>
    </svg>`;
  };

  const escapeXml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  // Print current label
  const printLabel = async () => {
    if (labels.length === 0) return;

    try {
      setIsRendering(true);
      const svgContent = await getPrintSVG(previewIndex);
      const dims = getLabelDimensions();

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) return;

      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Label</title>
            <style>
              @page {
                size: ${dims.width || 2}in ${dims.height || 1}in;
                margin: 0;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              @media print {
                body {
                  margin: 0 !important;
                  padding: 0 !important;
                  width: ${dims.width || 2}in;
                  height: ${dims.height || 1}in;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                svg {
                  width: 100% !important;
                  height: 100% !important;
                  display: block !important;
                  max-width: none !important;
                  max-height: none !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
              body {
                margin: 0;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                width: ${dims.width || 2}in;
                height: ${dims.height || 1}in;
              }
              svg {
                width: 100%;
                height: 100%;
                display: block;
              }
            </style>
          </head>
          <body>
            ${svgContent}
          </body>
        </html>
      `);
      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }, 100);

      addToast({
        type: 'success',
        title: 'Printing label',
        message: 'Print dialog opened',
        duration: 2000,
      });

    } catch (error) {
      console.error('Failed to print label:', error);
      addToast({
        type: 'error',
        title: 'Print failed',
        message: 'Could not generate label for printing',
        duration: 3000,
      });
    } finally {
      setIsRendering(false);
    }
  };

  // Print all labels
  const printAllLabels = async () => {
    if (labels.length === 0) return;

    setIsRendering(true);
    try {
      const dims = getLabelDimensions();
      const labelSvgs: string[] = [];

      for (let i = 0; i < labels.length; i++) {
        const svg = await getPrintSVG(i);
        labelSvgs.push(svg);
      }

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) return;

      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Labels</title>
            <style>
              @page {
                size: ${dims.width || 2}in ${dims.height || 1}in;
                margin: 0;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              @media print {
                body {
                  margin: 0 !important;
                  padding: 0 !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .label-page {
                  width: ${dims.width || 2}in;
                  height: ${dims.height || 1}in;
                  page-break-after: always;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                .label-page:last-child { page-break-after: avoid; }
                .label-page svg {
                  width: 100% !important;
                  height: 100% !important;
                  display: block !important;
                  max-width: none !important;
                  max-height: none !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
              body { margin: 0; padding: 0; }
              .label-page {
                width: ${dims.width || 2}in;
                height: ${dims.height || 1}in;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .label-page svg {
                display: block;
              }
            </style>
          </head>
          <body>
            ${labelSvgs.map((svg) => `
              <div class="label-page">
                ${svg}
              </div>
            `).join('')}
          </body>
        </html>
      `);
      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }, 500);

      addToast({
        type: 'success',
        title: `Printing ${labels.length} label${labels.length > 1 ? 's' : ''}`,
        duration: 3000,
      });

    } catch (error) {
      console.error('Failed to print labels:', error);
      addToast({
        type: 'error',
        title: 'Print failed',
        message: 'Could not generate labels for printing',
        duration: 3000,
      });
    } finally {
      setIsRendering(false);
    }
  };

  const prevLabel = () => {
    setPreviewIndex(i => (i > 0 ? i - 1 : labels.length - 1));
  };

  const nextLabel = () => {
    setPreviewIndex(i => (i < labels.length - 1 ? i + 1 : 0));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Label Printing</h1>
          <p className="text-foreground-muted">
            {labels.length} label{labels.length !== 1 ? 's' : ''} in queue
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="h-4 w-4" />
            Settings
          </Button>
          {labels.length > 0 && (
            <>
              <Button
                variant="secondary"
                onClick={clearQueue}
              >
                <Trash2 className="h-4 w-4" />
                Clear Queue
              </Button>
              <Button onClick={printAllLabels} disabled={isRendering}>
                <Printer className="h-4 w-4" />
                {isRendering ? 'Generating...' : `Print All (${labels.length})`}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Printer Settings</h2>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => {
                  const preset = presets.find(p => p.id === settings.labelPreset);
                  if (preset) {
                    openEditPresetModal(preset);
                  }
                }} disabled={!settings.labelPreset}>
                  <Edit2 className="h-4 w-4" />
                  Edit Preset
                </Button>
                <Button size="sm" onClick={saveSettings} loading={savingSettings} disabled={loadingSettings}>
                  <Save className="h-4 w-4" />
                  Save Settings
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* LEFT COLUMN - PRESETS */}
              <div className="space-y-4">
                {/* ACTIVE PRESET */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-foreground">Active Preset</h3>
                    <Button size="sm" variant="secondary" onClick={() => openCreatePresetModal(false)}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  <Select
                    options={presets.map(p => ({ value: p.id, label: `${p.name} (${p.width}" × ${p.height}")` }))}
                    value={settings.labelPreset}
                    onChange={e => applyPresetSettings(e.target.value)}
                  />
                  <div className="grid grid-cols-5 gap-2 mt-2 text-xs">
                    <div className="bg-surface-dark p-2 rounded">
                      <div className="text-foreground-muted mb-1">Width</div>
                      <div className="font-semibold">{currentPreset?.width || '2'}"</div>
                    </div>
                    <div className="bg-surface-dark p-2 rounded">
                      <div className="text-foreground-muted mb-1">Height</div>
                      <div className="font-semibold">{currentPreset?.height || '1'}"</div>
                    </div>
                    <div className="bg-surface-dark p-2 rounded">
                      <div className="text-foreground-muted mb-1">DPI</div>
                      <div className="font-semibold">{currentPreset?.dpi || 203}</div>
                    </div>
                    <div className="bg-surface-dark p-2 rounded">
                      <div className="text-foreground-muted mb-1">Code</div>
                      <div className="font-semibold">{currentPreset?.code_type === 'qrcode' ? 'QR' : 'Barcode'}</div>
                    </div>
                    <div className="bg-surface-dark p-2 rounded">
                      <div className="text-foreground-muted mb-1">Size</div>
                      <div className="font-semibold">{currentPreset?.code_size || 40}%</div>
                    </div>
                  </div>
                </div>

                {/* MANAGE PRESETS */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">All Presets ({presets.length})</h3>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => setShowManagePresetsModal(true)}
                  >
                    <Edit2 className="h-4 w-4" />
                    Manage All Presets
                  </Button>
                </div>

                {/* DEFAULT PRESETS */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Default Presets</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      label="Location"
                      options={presets.map(p => ({ value: p.id, label: p.name }))}
                      value={settings.locationLabelPreset}
                      onChange={e => setSettings(s => ({ ...s, locationLabelPreset: e.target.value }))}
                    />
                    <Select
                      label="Individual"
                      options={presets.map(p => ({ value: p.id, label: p.name }))}
                      value={settings.itemLabelPreset}
                      onChange={e => setSettings(s => ({ ...s, itemLabelPreset: e.target.value }))}
                    />
                    <Select
                      label="Bin"
                      options={presets.map(p => ({ value: p.id, label: p.name }))}
                      value={settings.binLabelPreset}
                      onChange={e => setSettings(s => ({ ...s, binLabelPreset: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - FINE-TUNING ADJUSTMENTS */}
              <div className="space-y-4">
                {/* TEXT SIZES */}
                <div className="border border-border rounded-lg p-4 bg-surface-dark">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Text Sizes</h3>
                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      label="Base (pt)"
                      type="text"
                      value={settings.fontSize}
                      onChange={e => setSettings(s => ({ ...s, fontSize: e.target.value }))}
                    />
                    <Input
                      label="Title (×)"
                      type="text"
                      value={settings.titleSizeMultiplier}
                      onChange={e => setSettings(s => ({ ...s, titleSizeMultiplier: e.target.value }))}
                    />
                    <Input
                      label="Location (×)"
                      type="text"
                      value={settings.locationSizeMultiplier}
                      onChange={e => setSettings(s => ({ ...s, locationSizeMultiplier: e.target.value }))}
                    />
                    <Input
                      label="Subtitle (×)"
                      type="text"
                      value={settings.subtitleSizeMultiplier}
                      onChange={e => setSettings(s => ({ ...s, subtitleSizeMultiplier: e.target.value }))}
                    />
                  </div>
                </div>

                {/* SPACING & POSITION */}
                <div className="border border-border rounded-lg p-4 bg-surface-dark">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Spacing & Position</h3>
                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      label="Text→Code"
                      type="text"
                      value={settings.textCodeGap}
                      onChange={e => setSettings(s => ({ ...s, textCodeGap: e.target.value }))}
                    />
                    <Input
                      label="Code→Sub"
                      type="text"
                      value={settings.codeSubtitleGap}
                      onChange={e => setSettings(s => ({ ...s, codeSubtitleGap: e.target.value }))}
                    />
                    <Input
                      label="X Offset"
                      type="text"
                      value={settings.offsetX}
                      onChange={e => setSettings(s => ({ ...s, offsetX: e.target.value }))}
                    />
                    <Input
                      label="Y Offset"
                      type="text"
                      value={settings.offsetY}
                      onChange={e => setSettings(s => ({ ...s, offsetY: e.target.value }))}
                    />
                  </div>
                </div>

                {/* OPTIONS */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Options</h3>
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={settings.showLocation}
                      onChange={e => setSettings(s => ({ ...s, showLocation: e.target.checked }))}
                      className="rounded border-border w-3 h-3"
                    />
                    Show location on label
                  </label>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Preset Modal */}
      {showPresetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-surface rounded-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editingPreset
                ? (isCreatingSystemPreset ? 'Edit System Preset' : 'Edit Preset')
                : (isCreatingSystemPreset ? 'Create System Preset' : 'Create Personal Preset')
              }
            </h2>
            {isCreatingSystemPreset && (
              <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="font-medium">System Preset:</span>
                  <span className="text-foreground-muted">Will be deployed to all users</span>
                </div>
              </div>
            )}
            <div className="space-y-4">
              <Input
                label="Preset Name"
                value={presetForm.name}
                onChange={e => setPresetForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Small Asset Tag"
              />

              {/* Label Dimensions */}
              <div className="border border-border rounded-lg p-4 bg-surface-dark">
                <h3 className="text-sm font-semibold mb-3 text-foreground">Label Dimensions</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Width (inches)"
                    type="text"
                    value={presetForm.width}
                    onChange={e => setPresetForm(f => ({ ...f, width: e.target.value }))}
                    placeholder="e.g., 2"
                  />
                  <Input
                    label="Height (inches)"
                    type="text"
                    value={presetForm.height}
                    onChange={e => setPresetForm(f => ({ ...f, height: e.target.value }))}
                    placeholder="e.g., 1"
                  />
                </div>
              </div>

              {/* Barcode Parameters */}
              <div className="border border-border rounded-lg p-4 bg-surface-dark">
                <h3 className="text-sm font-semibold mb-3 text-foreground">Barcode Parameters</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="DPI"
                      type="text"
                      value={presetForm.dpi}
                      onChange={e => setPresetForm(f => ({ ...f, dpi: e.target.value }))}
                      placeholder="203"
                    />
                    <Input
                      label="Code Size (%)"
                      type="text"
                      value={presetForm.codeSize}
                      onChange={e => setPresetForm(f => ({ ...f, codeSize: e.target.value }))}
                      placeholder="40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Code Type</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="presetCodeType"
                          value="barcode"
                          checked={presetForm.codeType === 'barcode'}
                          onChange={() => setPresetForm(f => ({ ...f, codeType: 'barcode' }))}
                          className="w-4 h-4"
                        />
                        Barcode
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="presetCodeType"
                          value="qrcode"
                          checked={presetForm.codeType === 'qrcode'}
                          onChange={() => setPresetForm(f => ({ ...f, codeType: 'qrcode' }))}
                          className="w-4 h-4"
                        />
                        QR Code
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowPresetModal(false);
                  setEditingPreset(null);
                  setPresetForm({ name: '', width: '', height: '', dpi: '203', codeType: 'barcode', codeSize: '40' });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePresetSubmit}
                loading={createPresetMutation.isPending || updatePresetMutation.isPending}
              >
                {editingPreset ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Presets Modal */}
      {showManagePresetsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Manage Label Presets</h2>
              <div className="flex gap-2">
                {isAdmin && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openCreatePresetModal(true)}
                  >
                    <Users className="h-4 w-4" />
                    Add System Preset
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => openCreatePresetModal(false)}
                >
                  <Plus className="h-4 w-4" />
                  Add Personal
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {presetsLoading ? (
                <div className="text-center py-8">
                  <div className="spinner h-6 w-6 mx-auto" />
                </div>
              ) : presets.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {presets.map((preset) => (
                    <div
                      key={preset.id}
                      className="p-4 border border-border rounded-lg hover:border-primary transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base truncate">{preset.name}</h3>
                          <p className="text-sm text-foreground-muted">
                            {preset.width}" × {preset.height}" {preset.is_system ? '(System)' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {(preset.is_system === 0 || isAdmin) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                openEditPresetModal(preset);
                                setShowManagePresetsModal(false);
                              }}
                              title="Edit"
                              className="h-8 w-8"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                          {preset.is_system === 0 && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => duplicatePresetMutation.mutate(preset.id)}
                              title="Duplicate"
                              className="h-8 w-8"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                          {(preset.is_system === 0 || isAdmin) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                const confirmMessage = preset.is_system === 1
                                  ? `Delete system preset "${preset.name}" from all users?`
                                  : `Delete preset "${preset.name}"?`;
                                if (confirm(confirmMessage)) {
                                  if (preset.is_system === 1) {
                                    deleteSystemPresetMutation.mutate({
                                      name: preset.name,
                                      width: preset.width,
                                      height: preset.height,
                                    });
                                  } else {
                                    deletePresetMutation.mutate(preset.id);
                                  }
                                }
                              }}
                              title="Delete"
                              className="h-8 w-8"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-foreground-muted mt-2">
                        <div>
                          <span className="font-medium">DPI:</span> {preset.dpi || 203}
                        </div>
                        <div>
                          <span className="font-medium">Code:</span> {preset.code_type === 'qrcode' ? 'QR' : 'Barcode'}
                        </div>
                        <div>
                          <span className="font-medium">Size:</span> {preset.code_size || 40}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-foreground-muted">
                  <Tags className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No presets yet</p>
                  <p className="text-sm">Click "Add New" to create your first preset</p>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-4 pt-4 border-t border-border">
              <Button onClick={() => setShowManagePresetsModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loadingSettings || presetsLoading ? (
        <Card>
          <CardBody className="text-center py-12">
            <div className="spinner h-8 w-8 mx-auto mb-4" />
            <p className="text-foreground-muted">Loading printer settings...</p>
          </CardBody>
        </Card>
      ) : labels.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Preview */}
          <Card>
            <CardBody>
              <h2 className="text-lg font-semibold mb-4">Preview</h2>

              <div className="bg-gray-100 p-4 rounded-lg mb-4 flex items-center justify-center">
                <canvas
                  ref={canvasRef}
                  className="border border-gray-300 shadow-lg bg-white"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>

              {/* Preview Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={prevLabel}
                  disabled={labels.length <= 1 || isRendering}
                >
                  <ChevronUp className="h-4 w-4" />
                  Previous
                </Button>

                <div className="text-sm text-foreground-muted">
                  {previewIndex + 1} of {labels.length}
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={nextLabel}
                  disabled={labels.length <= 1 || isRendering}
                >
                  Next
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <Button
                  className="w-full"
                  onClick={printLabel}
                  disabled={isRendering}
                >
                  <Printer className="h-4 w-4" />
                  {isRendering ? 'Generating Label...' : 'Print This Label'}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Queue */}
          <Card>
            <CardBody>
              <h2 className="text-lg font-semibold mb-4">Print Queue</h2>

              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {labels.map((label, idx) => (
                  <div
                    key={`${label.code}-${idx}`}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      idx === previewIndex
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setPreviewIndex(idx)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-mono bg-surface px-2 py-0.5 rounded">
                            {label.code}
                          </span>
                          <span className="text-xs text-foreground-muted capitalize">
                            {label.type}
                          </span>
                        </div>
                        <p className="font-medium text-sm truncate">{label.title}</p>
                        {label.subtitle && (
                          <p className="text-xs text-foreground-muted truncate">{label.subtitle}</p>
                        )}
                        {label.location && settings.showLocation && (
                          <p className="text-xs text-foreground-muted italic truncate">
                            {label.location}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewIndex(idx);
                            setTimeout(() => printLabel(), 50);
                          }}
                          title="Print this label"
                          disabled={isRendering}
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLabel(label.code);
                            if (previewIndex >= labels.length - 1) {
                              setPreviewIndex(Math.max(0, labels.length - 2));
                            }
                          }}
                          title="Remove from queue"
                          disabled={isRendering}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <Card>
          <CardBody className="text-center py-12">
            <Printer className="h-16 w-16 mx-auto mb-4 opacity-50 text-foreground-muted" />
            <h3 className="text-lg font-medium mb-2">No labels in queue</h3>
            <p className="text-sm text-foreground-muted">
              Add labels from the Inventory or Locations pages.
            </p>
          </CardBody>
        </Card>
      )}

      {/* Instructions */}
      {!loadingSettings && !presetsLoading && (
        <Card>
          <CardBody>
            <h3 className="font-semibold mb-2">Printing Instructions</h3>
            <div className="text-sm text-foreground-muted space-y-1">
              <p>1. Manage your label size presets in Settings - add, edit, duplicate, or delete presets</p>
              <p>2. Set default presets for different label types (locations, items, bins)</p>
              <p>3. Configure code type (barcode/QR), text sizes, and spacing in Settings</p>
              <p>4. Add items to the print queue from Inventory or Locations pages</p>
              <p>5. Preview labels and adjust settings to maximize code visibility</p>
              <p>6. Click "Print This Label" or "Print All" to open the print dialog</p>
              <p>7. Click "Save Settings" to save your preferences to your account</p>
              <p>8. <strong>IMPORTANT:</strong> Make sure "Print Background Graphics" is enabled in your browser print settings</p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
